import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { toast } from 'sonner'
import { Userpilot } from 'userpilot'
import { needsAnalysisService } from '@/services/needsAnalysisService'
import { NeedsAnalysisContext } from './NeedsAnalysisContext'
import { useConditionalQuestions, useScreenSequence } from './useScreenSequence'
import { useFormValidation } from './useFormValidation'
import { useNeedsAnalysis as useNeedsAnalysisData } from '@/hooks/data/needs-analysis/useNeedsAnalysis'
import { DEFAULT_COMMUNICATION_CHANNELS, DEFAULT_COMMUNICATION_FREQUENCY } from '@/constants/needsAnalysis'
import type { NeedsAnalysisState, NeedsAnalysisContextType } from './NeedsAnalysisContext'
import type {
  CommunicationChannelPreference,
  CommunicationFrequencyPreference,
  NativeLanguage,
  SelfAssessedLevel,
  WeeklyTimeCommitment,
  PrimaryGoal,
  IndustryInterest,
  TestType,
  NeedsAnalysisResponse,
  NeedsAnalysisSubmissionRequest,
} from '@/types/learner-portal'

type NeedsAnalysisProviderProps = {
  readonly children: React.ReactNode
  readonly onSubmitSuccess?: () => void
}

// Initial state for the survey
const createInitialState = (): NeedsAnalysisState => ({
  native_language: null,
  self_assessed_english_level: null,
  weekly_time_commitment: null,
  primary_goals: [],
  goal_specification: null,
  industry_interests: [],
  communication_channels: DEFAULT_COMMUNICATION_CHANNELS,
  communication_frequency: DEFAULT_COMMUNICATION_FREQUENCY,
  currentStep: 0,
  isSubmitting: false,
  isDirty: false,
})

// The communication answers are pre-selected, so a saved answer must win over the
// defaults on every open or "Update my responses" would silently rewrite it.
const withExistingAnswers = (state: NeedsAnalysisState, existing: NeedsAnalysisResponse | null): NeedsAnalysisState => {
  if (!existing) return state

  const communication = {
    communication_channels:
      (existing.communication_channels as readonly CommunicationChannelPreference[] | null) ??
      DEFAULT_COMMUNICATION_CHANNELS,
    communication_frequency:
      (existing.communication_frequency as CommunicationFrequencyPreference | null) ?? DEFAULT_COMMUNICATION_FREQUENCY,
  }

  if (existing.completed_at) return { ...state, ...communication }

  // Saved but not completed - restore the learner's progress
  return {
    ...state,
    native_language: null, // Native language is stored in user table, not needs_analysis
    self_assessed_english_level: (existing.self_assessed_english_level as SelfAssessedLevel) || null,
    weekly_time_commitment: existing.weekly_time_commitment as WeeklyTimeCommitment,
    primary_goals: existing.primary_goals as readonly PrimaryGoal[],
    goal_specification: (existing.goal_specification as TestType) || null,
    industry_interests: (existing.industry_interests || []) as readonly IndustryInterest[],
    ...communication,
    isDirty: false,
  }
}

export function NeedsAnalysisProvider({ children, onSubmitSuccess }: NeedsAnalysisProviderProps) {
  const { getToken } = useAuth()
  const [state, setState] = useState<NeedsAnalysisState>(createInitialState())
  const { data: existingNeedsAnalysis, hasProficiencyAssessment, refetch } = useNeedsAnalysisData()

  // Determine if the English level question should be skipped (ENG-1990)
  const skipEnglishLevel = hasProficiencyAssessment

  useEffect(() => {
    setState((prev) => withExistingAnswers(prev, existingNeedsAnalysis))
  }, [existingNeedsAnalysis])

  // Calculate conditional questions and screen sequence
  const { showTestTypeQuestion } = useConditionalQuestions(state.primary_goals)
  const { screens, totalSteps } = useScreenSequence({
    showTestTypeQuestion,
    skipEnglishLevel,
  })

  // Form validation
  const { canProceedFromCurrentStep, isFormValid } = useFormValidation(state, screens)

  // Keep the step inside the sequence when a conditional screen disappears
  useEffect(() => {
    setState((prev) => (prev.currentStep > screens.length - 1 ? { ...prev, currentStep: screens.length - 1 } : prev))
  }, [screens.length])

  // Clear conditional data when their trigger goals are removed
  useEffect(() => {
    setState((prev) => {
      if (!showTestTypeQuestion && prev.goal_specification) {
        return { ...prev, goal_specification: null }
      }
      return prev
    })
  }, [showTestTypeQuestion])

  // Field setters
  const setNativeLanguage = useCallback((language: NativeLanguage | null) => {
    setState((prev) => ({ ...prev, native_language: language, isDirty: true }))
  }, [])

  const setEnglishLevel = useCallback((level: SelfAssessedLevel) => {
    setState((prev) => ({ ...prev, self_assessed_english_level: level, isDirty: true }))
  }, [])

  const setTimeCommitment = useCallback((time: WeeklyTimeCommitment) => {
    setState((prev) => ({ ...prev, weekly_time_commitment: time, isDirty: true }))
  }, [])

  const setGoals = useCallback((goals: PrimaryGoal[]) => {
    const limitedGoals = goals.slice(0, 2) as readonly PrimaryGoal[]
    setState((prev) => ({ ...prev, primary_goals: limitedGoals, isDirty: true }))
  }, [])

  const toggleIndustry = useCallback((industry: IndustryInterest) => {
    setState((prev) => {
      const newInterests = prev.industry_interests.includes(industry)
        ? (prev.industry_interests.filter((i) => i !== industry) as readonly IndustryInterest[])
        : ([...prev.industry_interests, industry] as readonly IndustryInterest[])
      return { ...prev, industry_interests: newInterests, isDirty: true }
    })
  }, [])

  const setTestType = useCallback((test: TestType | null) => {
    setState((prev) => ({ ...prev, goal_specification: test, isDirty: true }))
  }, [])

  const toggleCommunicationChannel = useCallback((channel: CommunicationChannelPreference) => {
    setState((prev) => {
      const channels = prev.communication_channels.includes(channel)
        ? prev.communication_channels.filter((c) => c !== channel)
        : [...prev.communication_channels, channel]
      return { ...prev, communication_channels: channels, isDirty: true }
    })
  }, [])

  const setCommunicationFrequency = useCallback((frequency: CommunicationFrequencyPreference) => {
    setState((prev) => ({ ...prev, communication_frequency: frequency, isDirty: true }))
  }, [])

  // Navigation functions
  const nextStep = useCallback(() => {
    if (!canProceedFromCurrentStep()) {
      toast.error('Please complete the current step before continuing')
      return
    }
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, screens.length - 1),
    }))
  }, [canProceedFromCurrentStep, screens.length])

  const previousStep = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, 0) }))
  }, [])

  const goToStep = useCallback(
    (step: number) => {
      setState((prev) => ({ ...prev, currentStep: Math.max(0, Math.min(step, screens.length - 1)) }))
    },
    [screens.length]
  )

  // Data conversion
  const getSubmissionData = useCallback((): NeedsAnalysisSubmissionRequest => {
    return {
      native_language: state.native_language || undefined,
      self_assessed_english_level: state.self_assessed_english_level || undefined,
      weekly_time_commitment: state.weekly_time_commitment!,
      primary_goals: [...state.primary_goals] as PrimaryGoal[],
      goal_specification: state.goal_specification || undefined,
      industry_interests:
        state.industry_interests.length > 0 ? ([...state.industry_interests] as IndustryInterest[]) : undefined,
      communication_channels: [...state.communication_channels],
      communication_frequency: state.communication_frequency ?? undefined,
    }
  }, [state])

  // Submit survey
  const submitSurvey = useCallback(async () => {
    if (!isFormValid()) {
      toast.error('Please complete all required fields')
      return
    }

    setState((prev) => ({ ...prev, isSubmitting: true }))

    try {
      const submissionData = getSubmissionData()
      await needsAnalysisService.submitNeedsAnalysis(submissionData, getToken)

      // Track needs analysis completion in Userpilot
      Userpilot.track('Needs Analysis Completed', {
        native_language: submissionData.native_language,
        self_assessed_level: submissionData.self_assessed_english_level,
        weekly_time_commitment: submissionData.weekly_time_commitment,
        primary_goals: submissionData.primary_goals,
        goal_specification: submissionData.goal_specification,
        industry_interests: submissionData.industry_interests,
        communication_channels: submissionData.communication_channels,
        communication_frequency: submissionData.communication_frequency,
      })

      toast.success('Profile completed successfully!')
      void refetch()
      onSubmitSuccess?.()
    } catch (error) {
      console.error('Failed to submit needs analysis:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save your profile. Please try again.')
      throw error
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }))
    }
  }, [isFormValid, getSubmissionData, getToken, onSubmitSuccess, refetch])

  // Reset function
  const reset = useCallback(() => {
    setState(withExistingAnswers(createInitialState(), existingNeedsAnalysis))
  }, [existingNeedsAnalysis])

  // Build context value
  const contextValue: NeedsAnalysisContextType = useMemo(
    () => ({
      ...state,
      screens,
      totalSteps,
      setNativeLanguage,
      setEnglishLevel,
      setTimeCommitment,
      setGoals,
      toggleIndustry,
      setTestType,
      toggleCommunicationChannel,
      setCommunicationFrequency,
      nextStep,
      previousStep,
      goToStep,
      reset,
      submitSurvey,
      canProceedFromCurrentStep,
      isFormValid,
      getSubmissionData,
    }),
    [
      state,
      screens,
      totalSteps,
      setNativeLanguage,
      setEnglishLevel,
      setTimeCommitment,
      setGoals,
      toggleIndustry,
      setTestType,
      toggleCommunicationChannel,
      setCommunicationFrequency,
      nextStep,
      previousStep,
      goToStep,
      reset,
      submitSurvey,
      canProceedFromCurrentStep,
      isFormValid,
      getSubmissionData,
    ]
  )

  return <NeedsAnalysisContext.Provider value={contextValue}>{children}</NeedsAnalysisContext.Provider>
}
